import { useApi, useToaster, type AdminUpdateDraftLeadRequest } from "@shopexperts/shared";
import { useMutation, useQueryClient } from "@tanstack/vue-query";

interface UpdateDraftLeadParams {
    id: number;
    data: AdminUpdateDraftLeadRequest;
}

/**
 * Composable for updating a draft lead (admin)
 */
export function useUpdateDraftLead() {
    const api = useApi();
    const toaster = useToaster();
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
        mutationFn: ({ id, data }: UpdateDraftLeadParams) => {
            return api.admin.draftLeads.updateDraftLead({
                params: { id },
                body: data,
            });
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["admin", "draft-leads"] });
            queryClient.invalidateQueries({ queryKey: ["admin", "draft-leads", variables.id] });
            toaster.create({
                title: "Draft lead updated successfully",
                type: "success",
            });
        },
        onError: (error) => {
            toaster.create({
                title: "Failed to update draft lead",
                description: error.message || "An error occurred",
                type: "error",
            });
            console.error("[useUpdateDraftLead] Error:", error);
        },
    });

    return {
        mutate,
        mutateAsync,
        isPending,
        error,
        data,
        reset,
    };
}
